import { useState, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { axiosInstance } from "@/App";
import { toast } from "sonner";
import { MapPin, Users, DollarSign, Calendar, ArrowLeft, Bell, BellOff, ExternalLink } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";

const CampsiteDetail = ({ user }) => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [campsite, setCampsite] = useState(null);
  const [loading, setLoading] = useState(true);
  const [isWatching, setIsWatching] = useState(false);
  const [watchLoading, setWatchLoading] = useState(false);
  const [showWatchForm, setShowWatchForm] = useState(false);
  const [watchData, setWatchData] = useState({
    site_number: "",
    start_date: "",
    end_date: ""
  });

  useEffect(() => {
    fetchCampsite();
    if (user) {
      checkWatchStatus();
    }
  }, [id, user]);

  const fetchCampsite = async () => {
    try {
      const response = await axiosInstance.get(`/campsites/${id}`);
      setCampsite(response.data);
    } catch (error) {
      toast.error("Failed to load campsite details");
      navigate("/");
    } finally {
      setLoading(false);
    }
  };

  const checkWatchStatus = async () => {
    try {
      const response = await axiosInstance.get("/watch");
      const watching = response.data.some(site => site.campsite_id === id);
      setIsWatching(watching);
    } catch (error) {
      console.error("Failed to check watch status");
    }
  };

  const handleWatchClick = () => {
    if (!user) {
      toast.error("Please sign in to watch campsites");
      const redirectUrl = `${window.location.origin}/dashboard`;
      window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
      return;
    }
    setShowWatchForm(true);
  };

  const handleWatch = async (e) => {
    e.preventDefault();
    
    if (!watchData.site_number || !watchData.start_date || !watchData.end_date) {
      toast.error("Please fill in all fields");
      return;
    }

    setWatchLoading(true);
    try {
      await axiosInstance.post("/watch", { 
        campsite_id: id,
        site_number: parseInt(watchData.site_number),
        start_date: watchData.start_date,
        end_date: watchData.end_date
      });
      setIsWatching(true);
      setShowWatchForm(false);
      toast.success("Now watching! You'll be notified when this site becomes available.");
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to watch site");
    } finally {
      setWatchLoading(false);
    }
  };

  const handleUnwatch = async () => {
    setWatchLoading(true);
    try {
      await axiosInstance.delete(`/watch/${id}`);
      setIsWatching(false);
      toast.success("Stopped watching this campsite");
    } catch (error) {
      toast.error("Failed to stop watching");
    } finally {
      setWatchLoading(false);
    }
  };

  const handleBookNow = () => {
    window.open(campsite.booking_url, '_blank');
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen bg-gradient-to-br from-orange-50 to-beige-100">
        <div className="text-xl font-medium">Loading...</div>
      </div>
    );
  }

  if (!campsite) return null;

  return (
    <div className="min-h-screen bg-gradient-to-br from-orange-50 via-beige-50 to-orange-100">
      {/* Header */}
      <header className="glass-card mx-6 my-4 px-8 py-4">
        <div className="max-w-7xl mx-auto flex justify-between items-center">
          <div className="flex items-center space-x-4">
            <Button variant="ghost" onClick={() => navigate("/")} data-testid="back-button">
              <ArrowLeft className="w-5 h-5" />
            </Button>
            <div className="flex items-center space-x-2">
              <img src="https://customer-assets.emergentagent.com/job_sitehunter-1/artifacts/rlsp35z7_CampPing%20UI%20Icon.png" alt="CampSight" className="w-10 h-10" />
              <h1 className="text-2xl font-bold text-gray-900" style={{fontFamily: 'Lobster Two, cursive', fontWeight: 700}}>CampSight</h1>
            </div>
          </div>
          {user && (
            <Button onClick={() => navigate("/dashboard")} className="btn-secondary" data-testid="dashboard-button">
              My Dashboard
            </Button>
          )}
        </div>
      </header>

      {/* Main Content */}
      <div className="max-w-6xl mx-auto px-6 py-8">
        <div className="glass-card p-8">
          {/* Hero Image */}
          <img 
            src={campsite.images[0]} 
            alt={campsite.name} 
            className="w-full h-96 object-cover rounded-2xl mb-8"
          />

          {/* Closed Status Banner */}
          {!campsite.available && (
            <div className="bg-red-50 border-l-4 border-red-600 p-6 mb-6 rounded-lg">
              <div className="flex items-center">
                <div className="flex-shrink-0">
                  <svg className="h-6 w-6 text-red-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
                  </svg>
                </div>
                <div className="ml-3">
                  <h3 className="text-lg font-semibold text-red-800">Park Currently Closed</h3>
                  <p className="text-red-700 mt-1">
                    This park is closed for the winter season. You can still set up alerts to be notified when it reopens in the spring!
                  </p>
                </div>
              </div>
            </div>
          )}

          {/* Title and Badge */}
          <div className="flex justify-between items-start mb-6">
            <div>
              <h2 className="text-4xl font-bold text-gray-900 mb-2" data-testid="campsite-name">{campsite.name}</h2>
              <div className="text-lg text-gray-600 flex items-center">
                <MapPin className="w-5 h-5 mr-2" />
                {campsite.park_name}, {campsite.location}
              </div>
            </div>
            <Badge className="text-lg px-4 py-2 bg-orange-600">{campsite.site_type}</Badge>
          </div>

          {/* Description */}
          <p className="text-gray-700 text-lg mb-8">{campsite.description}</p>

          {/* Details Grid */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-8">
            <div className="card">
              <div className="flex items-center space-x-3 mb-2">
                <Users className="w-6 h-6 text-orange-600" />
                <span className="font-semibold">Max Occupancy</span>
              </div>
              <p className="text-2xl font-bold" data-testid="max-occupancy">{campsite.max_occupancy} guests</p>
            </div>
            <div className="card">
              <div className="flex items-center space-x-3 mb-2">
                <DollarSign className="w-6 h-6 text-orange-600" />
                <span className="font-semibold">Price per Night</span>
              </div>
              <p className="text-2xl font-bold text-orange-600" data-testid="price">${campsite.price_per_night}</p>
            </div>
            <div className="card">
              <div className="flex items-center space-x-3 mb-2">
                <Calendar className="w-6 h-6 text-orange-600" />
                <span className="font-semibold">Status</span>
              </div>
              {campsite.available ? (
                <p className="text-2xl font-bold text-green-600">Open Now</p>
              ) : (
                <div>
                  <p className="text-2xl font-bold text-red-600">Closed for Season</p>
                  <p className="text-sm text-gray-600 mt-1">Check back in spring</p>
                </div>
              )}
            </div>
          </div>

          {/* Amenities */}
          <div className="mb-8">
            <h3 className="text-2xl font-bold mb-4">Amenities</h3>
            <div className="flex flex-wrap gap-2">
              {campsite.amenities.map((amenity, index) => (
                <Badge key={index} variant="outline" className="px-4 py-2 text-sm" data-testid={`amenity-${index}`}>
                  {amenity}
                </Badge>
              ))}
            </div>
          </div>

          {/* Action Buttons */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {!isWatching ? (
              <Button 
                onClick={handleWatchClick} 
                disabled={watchLoading}
                className="w-full text-lg py-6"
                style={{background: 'linear-gradient(135deg, #2D5016 0%, #0D7377 100%)', color: 'white'}}
                data-testid="watch-button"
              >
                <Bell className="w-5 h-5 mr-2" />
                Watch Specific Site
              </Button>
            ) : (
              <Button 
                onClick={handleUnwatch} 
                disabled={watchLoading}
                variant="outline"
                className="btn-secondary w-full text-lg py-6"
                data-testid="unwatch-button"
              >
                <BellOff className="w-5 h-5 mr-2" />
                Stop Watching
              </Button>
            )}
            <Button 
              onClick={handleBookNow}
              className="btn-primary w-full text-lg py-6"
              data-testid="book-now-button"
            >
              <ExternalLink className="w-5 h-5 mr-2" />
              Book on Ontario Parks
            </Button>
          </div>
          
          {/* Watch Site Dialog */}
          <Dialog open={showWatchForm} onOpenChange={setShowWatchForm}>
            <DialogContent className="sm:max-w-md">
              <DialogHeader>
                <DialogTitle>Watch Specific Site at {campsite.park_name}</DialogTitle>
              </DialogHeader>
              <form onSubmit={handleWatch} className="space-y-4">
                <div>
                  <Label htmlFor="site_number">Site Number</Label>
                  <Input
                    id="site_number"
                    type="number"
                    placeholder="e.g., 45"
                    value={watchData.site_number}
                    onChange={(e) => setWatchData({...watchData, site_number: e.target.value})}
                    required
                    min="1"
                  />
                </div>
                <div>
                  <Label htmlFor="start_date">Start Date</Label>
                  <Input
                    id="start_date"
                    type="date"
                    value={watchData.start_date}
                    onChange={(e) => setWatchData({...watchData, start_date: e.target.value})}
                    required
                    min={new Date().toISOString().split('T')[0]}
                  />
                </div>
                <div>
                  <Label htmlFor="end_date">End Date</Label>
                  <Input
                    id="end_date"
                    type="date"
                    value={watchData.end_date}
                    onChange={(e) => setWatchData({...watchData, end_date: e.target.value})}
                    required
                    min={watchData.start_date || new Date().toISOString().split('T')[0]}
                  />
                </div>
                <Button 
                  type="submit" 
                  className="w-full"
                  style={{background: '#FF6B35'}}
                  disabled={watchLoading}
                >
                  Start Watching
                </Button>
              </form>
            </DialogContent>
          </Dialog>
          
          <p className="text-sm text-center mt-4" style={{color: '#3E3E3E'}}>
            Booking is handled directly through Ontario Parks reservation system
          </p>
        </div>
      </div>
    </div>
  );
};

export default CampsiteDetail;
